import React, { useContext, useState, useEffect, useMemo } from 'react';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { initializeSelection, generateUniqueId } from '../utils/courseDesignUtilities';
import QuestionDesignSectionMenu from '../atoms/QuestionDesignSectionMenu';
import QuestionDesignTopicMenu from '../atoms/QuestionDesignTopicMenu';
import QuestionDesignQuestionList from '../atoms/QuestionDesignQuestionList';
import QuestionCreationButton from '../atoms/QuestionCreationButton';
import QuestionTutorial from '../atoms/QuestionTutorial';
import ThesisDesign from '../molecules/ThesisDesign';
import QuestionLogicRepresentation from '../molecules/QuestionLogicRepresentation';
import QAndA from './QAndA'; // Import the QAndA component
import MasterSharedQuestions from './MasterSharedQuestions'; // Import the new component
import { AuthContext } from '../context/AuthContext';



const CourseDesignQuestionDesign = () => {
    const { courseDesignState, handleChange } = useContext(CourseDesignContext);
    const [selectedTopicDataIndex, setSelectedTopicDataIndex] = useState(null);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(() => {
        const savedIndex = courseDesignState.find(item => item.dataType === 'questionDesignMenuLastVisited')?.dataText;
        return savedIndex !== undefined ? parseInt(savedIndex) : 0;
    });
    const [selectedTopicIndex, setSelectedTopicIndex] = useState(() => {
        const savedIndex = courseDesignState.find(item => item.dataType === 'questionDesignSubMenuLastVisited')?.dataText;
        return savedIndex !== undefined ? parseInt(savedIndex) : 0;
    });
    // get user from AuthContext
    const { username } = useContext(AuthContext);
    console.log('user:', username);

    // logic for switching between design view, student view, and logic view
    const [viewMode, setViewMode] = useState('designer');

    // Add this new handler for the "Theses" click
const handleThesisClick = () => {
    setSelectedTopicDataIndex(null); // Deselect any topic
    setSelectedTopicIndex('thesisMode');
    setViewMode('thesisMode'); // Set view mode to thesisMode
};

// Add this new handler for the "Shared question library" click
const handleSharedQuestionsClick = () => {
    setSelectedTopicDataIndex(null); // Deselect any topic
    setSelectedTopicIndex('sharedQuestionsMode');
    setViewMode('sharedQuestionsMode'); // Set view mode to sharedQuestionsMode
};

// Add this new handler for the "How to create questions" click
const handleQuestionTutorialClick = () => {
    setSelectedTopicDataIndex(null); // Deselect any topic
    setSelectedTopicIndex('questionTutorialMode');
    setViewMode('questionTutorialMode'); // Set view mode to questionTutorialMode
};

// Handler for the three-way view mode toggle
const handleViewModeChange = (mode) => {
    setViewMode(mode);
};
      
const questions = useMemo(() => {
        return courseDesignState.filter(
            item => item.dataType === 'question' && item.questionCourseDate === selectedTopicDataIndex
        );
    }, [courseDesignState, selectedTopicDataIndex]); // Memoize based on courseDesignState and selectedTopicDataIndex




    const sections = courseDesignState
        .filter(item => item.dataType === 'sectionName')
        .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
//console.log('runs only on mount');
        initializeSelection(courseDesignState, sections, setSelectedSectionIndex, setSelectedTopicIndex, setSelectedTopicDataIndex, handleChange);
            // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    const getTopicsForSelectedSection = (sectionIndex = selectedSectionIndex) => {
        if (sectionIndex === null) return [];

        const currentSection = sections[sectionIndex];
        const nextSection = sections[sectionIndex + 1];

        return courseDesignState
            .filter(item =>
                item.dataType === 'topic' &&
                item.questionOptions === 'yes' &&
                parseInt(item.dataIndex) > parseInt(currentSection.dataIndex) &&
                (!nextSection || parseInt(item.dataIndex) < parseInt(nextSection.dataIndex))
            )
            .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));
    };

const handleSectionClick = (index) => {
    setSelectedSectionIndex(index);
    handleChange('questionDesignMenuLastVisited', '0', index.toString());

    const firstTopicInSection = getTopicsForSelectedSection(index)[0];
    if (firstTopicInSection) {
        setSelectedTopicIndex(parseInt(firstTopicInSection.dataIndex, 10));
        setSelectedTopicDataIndex(firstTopicInSection.dataID);
        handleChange('questionDesignSubMenuLastVisited', '0', firstTopicInSection.dataID);
    }
 // Switch to designer mode when clicking sections (from any special mode)
    setViewMode('designer');
};


    const handleTopicClick = (dataID) => {
        const selectedTopic = courseDesignState.find(item => item.dataType === 'topic' && item.dataID === dataID);
        if (selectedTopic) {
            setSelectedTopicDataIndex(selectedTopic.dataID);
            setSelectedTopicIndex(parseInt(selectedTopic.dataIndex, 10));
            handleChange('questionDesignSubMenuLastVisited', '0', selectedTopic.dataID);
            // Only switch to designer mode if we're not in logic or student mode
            if (viewMode !== 'logic' && viewMode !== 'student') {
                setViewMode('designer');
            }
            // If we're in logic or student mode, keep the view mode but the component will re-render with new day data
        }
    };

    const handleCreateQuestionClick = () => {
        console.log('Creating new question with index', questions.length + 1);
        const newId = generateUniqueId();
        const nextDataIndex = questions.length + 2;
        const newQuestion = {
            dataType: 'question',
            dataID: newId,
            dataText: '',
            dataIndex: nextDataIndex.toString(),
            questionType: 'LongAnswer',
            questionOptions: '',
            questionLogic: '',
            questionCourseDate: selectedTopicDataIndex
        };
        // Update courseDesignState directly instead of using setQuestions
        handleChange('question', newQuestion.dataID, '', {
            dataIndex: newQuestion.dataIndex,
            questionType: newQuestion.questionType,
            questionOptions: newQuestion.questionOptions,
            questionLogic: newQuestion.questionLogic,
            questionCourseDate: selectedTopicDataIndex
        });
    };


    return (
        <div className="question-design-wrapper">
            <QuestionDesignSectionMenu 
                sections={sections} 
                selectedSectionIndex={selectedSectionIndex} 
                handleSectionClick={handleSectionClick} 
                viewMode={viewMode}
            />
            <div>
                <div className="left-menu-bar">
                    <QuestionDesignTopicMenu 
                        topics={getTopicsForSelectedSection(selectedSectionIndex)} 
                        selectedTopicIndex={selectedTopicIndex}
                        handleTopicClick={handleTopicClick} 
                        handleThesisClick={handleThesisClick} // Add this handler
                        handleSharedQuestionsClick={handleSharedQuestionsClick} // Add this handler
                        handleQuestionTutorialClick={handleQuestionTutorialClick} // Add this handler
                    />
                </div>
               <div className="question-design-content">
    {viewMode === 'thesisMode' ? (
        <ThesisDesign />
    ) : viewMode === 'sharedQuestionsMode' ? (
        <MasterSharedQuestions />
    ) : viewMode === 'questionTutorialMode' ? (
        <QuestionTutorial />
    ) : (
        <>
            {/* Three-way segmented control for view mode */}
            <div className="view-mode-selector" style={{
                display: 'flex',
                backgroundColor: '#f0f0f0',
                borderRadius: '8px',
                padding: '4px',
                marginBottom: '20px',
                width: 'fit-content',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                gap: '4px' // Added gap between buttons
            }}>
                {[
                    { key: 'designer', label: 'Design', icon: '🛠️' },
                    { key: 'student', label: 'Student', icon: '👤' },
                    { key: 'logic', label: 'Logic', icon: '🔗' }
                ].map((option) => (
                    <button
                        key={option.key}
                        onClick={() => handleViewModeChange(option.key)}
                        style={{
                            padding: '8px 16px',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: viewMode === option.key ? 'var(--color1)' : 'var(--color2light)',
                            color: 'white',
                            fontWeight: viewMode === option.key ? '600' : '400',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '14px',
                            minWidth: '120px',
                            justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                            if (viewMode !== option.key) {
                                e.currentTarget.style.backgroundColor = 'var(--color2verylight)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (viewMode !== option.key) {
                                e.currentTarget.style.backgroundColor = 'var(--color2light)';
                            }
                        }}
                    >
                        <span>{option.icon}</span>
                        <span>{option.label}</span>
                    </button>
                ))}
            </div>

            {/* Logic View for selected day */}
            {viewMode === 'logic' && selectedTopicDataIndex ? (
                <div style={{ height: '800px', width: '100%' }}>
                    <QuestionLogicRepresentation 
                        key={selectedTopicDataIndex} // Force re-render when day changes
                        courseDesignState={courseDesignState}
                        filterByDate={selectedTopicDataIndex} // Filter by the selected topic's date
                        height="800px"
                        direction="TB"
                        showComplexityIndicators={true}
                        enableMiniMap={true}
                        onQuestionSelect={(question) => {
                            console.log('Selected question:', question);
                        }}
                    />
                </div>
            ) : viewMode === 'logic' && !selectedTopicDataIndex ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px',
                    color: '#666',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '2px dashed #dee2e6'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔗</div>
                    <h3 style={{ marginBottom: '10px', color: '#495057' }}>Select a Topic</h3>
                    <p>Choose a topic from the left menu to view its question dependencies</p>
                </div>
            ) : viewMode === 'designer' && selectedSectionIndex !== null && selectedTopicIndex !== null ? (
                <div>
                    <QuestionDesignQuestionList 
                        selectedTopicDataIndex={selectedTopicDataIndex} 
                    />
                    <QuestionCreationButton onCreate={handleCreateQuestionClick} />
                </div>
            ) : viewMode === 'student' ? (
                <div>
                    <QAndA
                        username={username}
                        firstSectionToShow={1}
                        limitToSpecificDay={true}
                        specificDayToShow={selectedTopicDataIndex}
                        ignoreCurrentCourseDate={true}
                    />
                </div>
            ) : null}
        </>
    )}
                </div>

            </div>
        </div>
    );
};

export default CourseDesignQuestionDesign;
